import React from 'react';
import { Navigate, RouteObject, redirect } from 'react-router-dom';
import { authService } from './services/auth';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import TenantIntro from './pages/TenantIntro';
import TenantIntroDetail from './pages/TenantIntroDetail';
import TenantIntroAddDetail from './pages/TenantIntroAddDetail';
import TenantIntroEdit from './pages/TenantIntroEdit';
import Notices from './pages/Notices';
import NoticeDetail from './pages/NoticeDetail';
import NoticeAdd from './pages/NoticeAdd';
import NoticeEdit from './pages/NoticeEdit';
import Restaurants from './pages/Restaurants';
import RestaurantAdd from './pages/RestaurantAdd';
import RestaurantDetail from './pages/RestaurantDetail';
import RestaurantEdit from './pages/RestaurantEdit';
import Popups from './pages/Popups';
import PopupAdd from './pages/PopupAdd';
import PopupDetail from './pages/PopupDetail';
import PopupEdit from './pages/PopupEdit';
import MediaManagement from './pages/MediaManagement';
import MediaAdd from './pages/MediaAdd';
import MediaDetail from './pages/MediaDetail';
import MediaEdit from './pages/MediaEdit';
import EventManagement from './pages/EventManagement';
import EventAdd from './pages/EventAdd';
import EventDetail from './pages/EventDetail';
import EventEdit from './pages/EventEdit';
import InteractionManagement from './pages/InteractionManagement';
import GreetingManagement from './pages/GreetingManagement';
import GreetingDetail from './pages/GreetingDetail';
import GreetingAdd from './pages/GreetingAdd';
import GreetingEdit from './pages/GreetingEdit';
import ApiManagement from './pages/ApiManagement';
import ContentApprovalManagement from './pages/ContentApprovalManagement';
import ContentApprovalDetail from './pages/ContentApprovalDetail';
import ContentApprovalEdit from './pages/ContentApprovalEdit';
import PermissionManagement from './pages/PermissionManagement';
import PermissionDetail from './pages/PermissionDetail';
import AddPermission from './pages/AddPermission';
import EditPermission from './pages/EditPermission';
import ScheduleManagement from './pages/ScheduleManagement';
import ScheduleDetail from './pages/ScheduleDetail';
import Reports from './pages/Reports';

// Protected Route Loader
const protectedLoader = () => {
  if (!authService.isAuthenticated()) {
    return redirect('/login');
  }
  return null;
};

// Role-based Route Loader
const roleBasedLoader = (allowedRoles: string[] = ['admin', 'super_admin', 'development_admin']) => {
  return () => {
    const user = authService.getCurrentUser();

    if (!authService.isAuthenticated()) {
      return redirect('/login');
    }

    if (!allowedRoles.includes(user?.role || '')) {
      return redirect(user?.role === 'admin' ? '/tenant-intro' : '/tenants');
    }

    return null;
  };
};

// Super Admin Only Loader (development_admin 포함)
const superAdminLoader = roleBasedLoader(['super_admin', 'development_admin']);

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />
  },
  {
    path: '/',
    element: <Layout />,
    loader: protectedLoader,
    children: [
      {
        index: true,
        element: <Dashboard />
      },
      {
        path: 'dashboard',
        element: <Dashboard />
      },

      // 콘텐츠 관리 - 건물 안내
      {
        path: 'tenants',
        element: <Tenants />,
        loader: superAdminLoader
      },
      {
        path: 'tenant-intro',
        element: <TenantIntro />
      },
      {
        path: 'tenant-intro/detail/:id',
        element: <TenantIntroDetail />
      },
      {
        path: 'tenant-intro/add',
        element: <TenantIntroAddDetail />
      },
      {
        path: 'tenant-intro/edit/:id',
        element: <TenantIntroEdit />
      },

      // 공지사항
      {
        path: 'notices',
        element: <Notices />
      },
      {
        path: 'notices/detail/:id',
        element: <NoticeDetail />
      },
      {
        path: 'notices/add',
        element: <NoticeAdd />
      },
      {
        path: 'notices/edit/:id',
        element: <NoticeEdit />
      },

      // 콘텐츠 관리 - 라이프&컬쳐
      {
        path: 'restaurants',
        element: <Restaurants />,
        loader: superAdminLoader
      },
      {
        path: 'restaurants/add',
        element: <RestaurantAdd />,
        loader: superAdminLoader
      },
      {
        path: 'restaurants/detail/:id',
        element: <RestaurantDetail />,
        loader: superAdminLoader
      },
      {
        path: 'restaurants/edit/:id',
        element: <RestaurantEdit />,
        loader: superAdminLoader
      },
      {
        path: 'popups',
        element: <Popups />,
        loader: superAdminLoader
      },
      {
        path: 'popups/add',
        element: <PopupAdd />,
        loader: superAdminLoader
      },
      {
        path: 'popups/detail/:id',
        element: <PopupDetail />,
        loader: superAdminLoader
      },
      {
        path: 'popups/edit/:id',
        element: <PopupEdit />,
        loader: superAdminLoader
      },

      // 콘텐츠 관리 - 미디어 아트
      {
        path: 'media-management',
        element: <MediaManagement />,
        loader: superAdminLoader
      },
      {
        path: 'media-management/add',
        element: <MediaAdd />,
        loader: superAdminLoader
      },
      {
        path: 'media-management/detail/:id',
        element: <MediaDetail />,
        loader: superAdminLoader
      },
      {
        path: 'media-management/edit/:id',
        element: <MediaEdit />,
        loader: superAdminLoader
      },

      // 이벤트 관리
      {
        path: 'event-management',
        element: <EventManagement />,
        loader: superAdminLoader
      },
      {
        path: 'event-management/add',
        element: <EventAdd />,
        loader: superAdminLoader
      },
      {
        path: 'event-management/detail/:id',
        element: <EventDetail />,
        loader: superAdminLoader
      },
      {
        path: 'event-management/edit/:id',
        element: <EventEdit />,
        loader: superAdminLoader
      },

      // 인터랙션 관리
      {
        path: 'interaction-management',
        element: <InteractionManagement />,
        loader: superAdminLoader
      },

      // 그리팅
      {
        path: 'greeting-management',
        element: <GreetingManagement />
      },
      {
        path: 'greeting-detail/:id',
        element: <GreetingDetail />
      },
      {
        path: 'greeting-add',
        element: <GreetingAdd />
      },
      {
        path: 'greeting-edit/:id',
        element: <GreetingEdit />
      },

      // 콘텐츠 관리 - 기타
      {
        path: 'api-management',
        element: <ApiManagement />,
        loader: superAdminLoader
      },
      {
        path: 'content-approval',
        element: <ContentApprovalManagement />,
        loader: superAdminLoader
      },
      {
        path: 'content-approval/detail/:id',
        element: <ContentApprovalDetail />,
        loader: superAdminLoader
      },
      {
        path: 'content-approval/edit/:id',
        element: <ContentApprovalEdit />,
        loader: superAdminLoader
      },

      // 상위 메뉴들
      {
        path: 'schedule-management',
        element: <ScheduleManagement />,
        loader: superAdminLoader
      },
      {
        path: 'schedule-management/detail',
        element: <ScheduleDetail />,
        loader: superAdminLoader
      },
      {
        path: 'permission-management',
        element: <PermissionManagement />,
        loader: superAdminLoader
      },
      {
        path: 'permission-management/detail/:id',
        element: <PermissionDetail />,
        loader: superAdminLoader
      },
      {
        path: 'permission-management/add',
        element: <AddPermission />,
        loader: superAdminLoader
      },
      {
        path: 'permission-management/edit/:id',
        element: <EditPermission />,
        loader: superAdminLoader
      },
      {
        path: 'reports',
        element: <Reports />,
        loader: superAdminLoader
      }
    ]
  }
];
